// Hand-rolled SQL pretty-printer for the CodeMirror editors.
//
// Why hand-rolled (no npm dependency): the project's supply-chain stance rules
// out adding a formatter package, and an "auto-format as you type" trigger has
// to run client-side with no round-trip — so a Go-side formatter is out too.
// This is a TOKENISER + clause/indent rules (the same shape sql-formatter et al.
// use under the hood), built on the quote/comment/backtick-aware scanning idiom
// already proven in lib/sqlsplit.js and lib/sqlparams.js.
//
// SAFETY INVARIANT — the whole reason this is trustworthy:
//   The formatter may ONLY change whitespace and KEYWORD CASE. It must never
//   add, drop, reorder, or alter any other token. We enforce that at runtime:
//   after formatting a statement we compare a "token signature" (every
//   non-whitespace token, with keyword case normalised) of the input vs the
//   output; on ANY mismatch we return the ORIGINAL statement untouched. So a
//   bug in the printer can make output ugly, but can never corrupt your SQL.
//
// Strings, comments, and backtick identifiers are copied verbatim (their bytes
// are never reflowed). DELIMITER blocks and CREATE/ALTER PROCEDURE/FUNCTION/
// TRIGGER/EVENT bodies are treated as OPAQUE (passed through unchanged) — the
// procedural BEGIN…END case is exactly the one that's unsafe to reflow.

// ── Keyword catalogue ───────────────────────────────────────────────────────
// Only words in this set are re-cased; anything else (identifiers, function
// names) keeps its original case. Missing a keyword is harmless — it just isn't
// uppercased. The set doesn't need to be exhaustive.
const KEYWORDS = new Set([
  'SELECT','FROM','WHERE','GROUP','BY','ORDER','HAVING','LIMIT','OFFSET',
  'INSERT','INTO','VALUES','VALUE','UPDATE','SET','DELETE','REPLACE','RETURNING',
  'JOIN','INNER','LEFT','RIGHT','FULL','OUTER','CROSS','NATURAL','STRAIGHT_JOIN',
  'ON','USING','AS','AND','OR','XOR','NOT','IN','IS','NULL','LIKE','RLIKE','REGEXP',
  'BETWEEN','EXISTS','ALL','ANY','SOME','DISTINCT','DISTINCTROW','UNION','EXCEPT','INTERSECT',
  'CASE','WHEN','THEN','ELSE','END','CAST','CONVERT','COLLATE','INTERVAL','SEPARATOR',
  'CREATE','ALTER','DROP','RENAME','TABLE','VIEW','INDEX','TRIGGER','PROCEDURE','FUNCTION',
  'EVENT','DATABASE','SCHEMA','TEMPORARY','DEFINER','IF','EXISTS',
  'PRIMARY','KEY','FOREIGN','REFERENCES','UNIQUE','CONSTRAINT','DEFAULT','AUTO_INCREMENT',
  'CHECK','CASCADE','RESTRICT','ENGINE','CHARSET','CHARACTER',
  'WITH','RECURSIVE','ASC','DESC','TRUE','FALSE','UNKNOWN','DUPLICATE','IGNORE','INTO',
  'BEGIN','DECLARE','CALL','ELSEIF','WHILE','LOOP','REPEAT','UNTIL','DO','LEAVE','ITERATE','RETURN','DELIMITER',
  'GRANT','REVOKE','USE','SHOW','DESCRIBE','EXPLAIN','TRUNCATE','LOCK','UNLOCK',
  'START','TRANSACTION','COMMIT','ROLLBACK','SAVEPOINT',
  'UNSIGNED','SIGNED','ZEROFILL','GENERATED','STORED','VIRTUAL','ALWAYS',
  'INT','INTEGER','BIGINT','MEDIUMINT','SMALLINT','TINYINT','DECIMAL','NUMERIC','FLOAT','DOUBLE','REAL','BIT','BOOL','BOOLEAN',
  'CHAR','VARCHAR','BINARY','VARBINARY','TEXT','TINYTEXT','MEDIUMTEXT','LONGTEXT','BLOB','TINYBLOB','MEDIUMBLOB','LONGBLOB',
  'DATE','DATETIME','TIMESTAMP','TIME','YEAR','ENUM','JSON','GEOMETRY',
]);

// Clause keywords force a newline at the current clause indent.
const CLAUSE_KW = new Set([
  'SELECT','FROM','WHERE','GROUP','ORDER','HAVING','LIMIT','OFFSET',
  'VALUES','SET','UNION','EXCEPT','INTERSECT','RETURNING',
]);

// Join keywords: the FIRST in a run starts a new line; the rest stay inline
// (so "LEFT OUTER JOIN" is one line). OUTER is included so it doesn't re-break.
const JOIN_KW = new Set([
  'JOIN','INNER','LEFT','RIGHT','FULL','OUTER','CROSS','NATURAL','STRAIGHT_JOIN',
]);

// :Name or :Name#type# query parameters (mirrors lib/sqlparams.js) — kept atomic
// so the formatter never splits "tgl#date#" apart.
const PARAM_RE = /^:([A-Za-z_][A-Za-z0-9_]*)(?:#([A-Za-z]+)#)?/;
const VAR_RE   = /^@@?[A-Za-z0-9_$.]+/;
const NUM_RE   = /^(?:0x[0-9a-fA-F]+|\d+\.?\d*(?:[eE][+-]?\d+)?|\.\d+(?:[eE][+-]?\d+)?)/;
// Multi-char operators, longest first.
const MULTI_OPS = ['<=>','->>','->','<<','>>','<=','>=','<>','!=',':=','||','&&'];

// ── Tokeniser ───────────────────────────────────────────────────────────────
// Produces significant tokens only (whitespace dropped — we regenerate it).
// Token types: kw | ident | number | string | dquote | param | var |
//              lineComment | blockComment | punct
// A keyword IMMEDIATELY followed by '(' (no whitespace) is reclassified as an
// identifier ("a function call") — this is how COUNT(*), LEFT(s,2), VALUES(x)
// are told apart from the LEFT JOIN / VALUES (…) clause forms, using the same
// signal the writer used: the space (or lack of it) before the paren.
export function tokenize(sql) {
  const toks = [];
  let i = 0;
  const n = sql.length;
  // Track the whitespace before each token so minimal mode can preserve the
  // writer's line breaks: pendNL = newlines in the run just skipped, pendInd =
  // the indentation after the last of them. Attached to the next token by push().
  let pendNL = 0, pendInd = '';
  // At every push() call site, `i` still points at the token's first char (it's
  // advanced past the token only AFTER the push), so [i, i+v.length) is the
  // token's source span — used by the case-only printer to splice in place.
  const push = (t, v) => { toks.push({ t, v, nl: pendNL, ind: pendInd, start: i, end: i + v.length }); pendNL = 0; pendInd = ''; };
  const isWordStart = (c) => /[A-Za-z_$-￿]/.test(c);
  const isWordPart  = (c) => /[A-Za-z0-9_$-￿]/.test(c);

  while (i < n) {
    const c = sql[i], nx = sql[i + 1];

    // whitespace — consume the whole run and remember its newlines + trailing
    // indent for the next token (minimal mode replays them; full mode ignores).
    if (c === ' ' || c === '\t' || c === '\r' || c === '\n') {
      let j = i;
      while (j < n && (sql[j] === ' ' || sql[j] === '\t' || sql[j] === '\r' || sql[j] === '\n')) j++;
      const run = sql.slice(i, j);
      const lastNl = run.lastIndexOf('\n');
      if (lastNl >= 0) { pendNL += (run.match(/\n/g) || []).length; pendInd = run.slice(lastNl + 1); }
      i = j;
      continue;
    }

    // comments
    if (c === '-' && nx === '-') { let j = i + 2; while (j < n && sql[j] !== '\n') j++; push('lineComment', sql.slice(i, j)); i = j; continue; }
    if (c === '#') { let j = i + 1; while (j < n && sql[j] !== '\n') j++; push('lineComment', sql.slice(i, j)); i = j; continue; }
    if (c === '/' && nx === '*') { let j = i + 2; while (j < n && !(sql[j] === '*' && sql[j + 1] === '/')) j++; j = Math.min(n, j + 2); push('blockComment', sql.slice(i, j)); i = j; continue; }

    // strings / quoted identifiers
    if (c === "'") { const r = readQuoted(sql, i, "'"); push('string', r.value); i = r.end; continue; }
    if (c === '"') { const r = readQuoted(sql, i, '"'); push('dquote', r.value); i = r.end; continue; }
    if (c === '`') { // a doubled `` inside is an escaped backtick, not the close
      let j = i + 1;
      while (j < n && (sql[j] !== '`' || sql[j + 1] === '`')) j += sql[j] === '`' ? 2 : 1;
      j = Math.min(n, j + 1); push('ident', sql.slice(i, j)); i = j; continue;
    }

    // query parameter :Name / :Name#type#
    if (c === ':') {
      const m = PARAM_RE.exec(sql.slice(i));
      if (m) { push('param', m[0]); i += m[0].length; continue; }
    }
    // user / system variable @x / @@x
    if (c === '@') {
      const m = VAR_RE.exec(sql.slice(i));
      if (m) { push('var', m[0]); i += m[0].length; continue; }
    }
    // number
    if (/[0-9]/.test(c) || (c === '.' && /[0-9]/.test(nx))) {
      const m = NUM_RE.exec(sql.slice(i));
      if (m) { push('number', m[0]); i += m[0].length; continue; }
    }
    // word (identifier or keyword)
    if (isWordStart(c)) {
      let j = i + 1;
      while (j < n && isWordPart(sql[j])) j++;
      const v = sql.slice(i, j);
      const funcLike = sql[j] === '(';                 // no space before '(' ⇒ function call
      const isKw = KEYWORDS.has(v.toUpperCase()) && !funcLike;
      push(isKw ? 'kw' : 'ident', v);
      i = j;
      continue;
    }
    // multi-char operator
    let matched = null;
    for (const op of MULTI_OPS) { if (sql.startsWith(op, i)) { matched = op; break; } }
    if (matched) { push('punct', matched); i += matched.length; continue; }
    // single punctuation / operator
    push('punct', c);
    i++;
  }
  return toks;
}

function readQuoted(sql, start, q) {
  const n = sql.length;
  let j = start + 1;
  while (j < n) {
    if (sql[j] === '\\' && j + 1 < n) { j += 2; continue; }
    if (sql[j] === q) { if (sql[j + 1] === q) { j += 2; continue; } j++; break; } // '' / "" doubling
    j++;
  }
  return { value: sql.slice(start, j), end: j };
}

// Token signature: the safety net. Keyword case is normalised (so re-casing
// passes); everything else must match byte-for-byte.
function sigToken(t) {
  if (t.t === 'kw') return 'K:' + t.v.toUpperCase();
  // A line comment captures up to the newline INCLUDING any trailing whitespace,
  // which the printer legitimately strips when it trims each rendered line. That
  // whitespace is insignificant, so ignore it here — otherwise a routine with a
  // trailing space after a `--`/`#` comment would never format (the invariant
  // would see the stripped comment as a changed token and bail).
  if (t.t === 'lineComment') return 'lineComment:' + t.v.replace(/[ 	]+$/, '');
  return t.t + ':' + t.v;
}
function signature(sql) {
  return tokenize(sql)
    .map(sigToken)
    .join('');
}

// ── Printer ─────────────────────────────────────────────────────────────────
function render(tok, opts) {
  if (tok.t !== 'kw') return tok.v;
  if (opts.keywordCase === 'lower') return tok.v.toLowerCase();
  if (opts.keywordCase === 'preserve') return tok.v;
  return tok.v.toUpperCase();
}

// Whether a single space goes between two adjacent tokens (newline rules in the
// main loop override this by emitting on a fresh line instead).
function spaceBetween(prev, cur) {
  if (!prev) return false;
  // A bare '@' punct only ever appears in `user@host` (DEFINER / GRANT / CREATE
  // USER) — `@var`/`@@sysvar` tokenize as 'var'. Keep it tight on both sides, like
  // '.', so `` `metalindo`@`%` `` doesn't become `` `metalindo` @ `%` `` (invalid).
  if (cur.t === 'punct' && cur.v === '@') return false;
  if (prev.t === 'punct' && prev.v === '@') return false;
  if (cur.t === 'punct' && (cur.v === ',' || cur.v === ';' || cur.v === ')' || cur.v === '.')) return false;
  if (prev.t === 'punct' && (prev.v === '(' || prev.v === '.')) return false;
  if (cur.t === 'punct' && cur.v === '(') {
    // function/group call sticks to the preceding value; keywords get a space.
    if (prev.t === 'ident' || prev.t === 'number' || prev.t === 'param' || prev.t === 'var' ||
        (prev.t === 'punct' && prev.v === ')')) return false;
    return true;
  }
  return true;
}

function isValueToken(t) {
  return t && (t.t === 'ident' || t.t === 'number' || t.t === 'string' || t.t === 'dquote' ||
               t.t === 'var' || t.t === 'param' || (t.t === 'punct' && t.v === ')'));
}

function printTokens(toks, opts) {
  const IND = opts.indent || '  ';
  let baseIndent = 0;        // clause indent (subqueries deepen it)
  let inlineParenDepth = 0;  // depth of NON-broken parens (suppresses comma breaks)
  let betweenDepth = 0;      // inside BETWEEN … AND (so that AND doesn't break)
  let currentClause = '';
  const frames = [];         // paren stack: { indent, broken }
  const lines = [];
  let line = '';
  let lineStarted = false;
  let prev = null;           // previously emitted token
  let prev2 = null;          // the one before that (to spot a table-name '(')
  let attachNext = false;    // unary sign: suppress the space before the next token

  const newline = (lvl) => {
    const pad = IND.repeat(Math.max(0, lvl));
    if (!lineStarted) { line = pad; return; }   // re-indent an empty line, no blank line
    lines.push(line.replace(/[ \t]+$/, ''));
    line = pad;
    lineStarted = false;
  };
  // forceSpace: true ⇒ always space (when mid-line), false ⇒ attach, undefined ⇒ use the rule.
  const put = (tok, text, forceSpace) => {
    let sp;
    if (forceSpace === true) sp = lineStarted;
    else if (forceSpace === false) sp = false;
    else sp = lineStarted && !attachNext && spaceBetween(prev, tok);
    line += (sp ? ' ' : '') + text;
    lineStarted = true;
    prev2 = prev;
    prev = tok;
    attachNext = false;
  };

  for (let idx = 0; idx < toks.length; idx++) {
    const tok = toks[idx];
    const text = render(tok, opts);

    if (tok.t === 'lineComment') { put(tok, text); newline(baseIndent); continue; }

    if (tok.t === 'kw') {
      const upper = tok.v.toUpperCase();
      if (upper === 'BETWEEN') { put(tok, text); betweenDepth++; continue; }
      if (upper === 'AND' || upper === 'OR' || upper === 'XOR') {
        if (upper === 'AND' && betweenDepth > 0) { put(tok, text); betweenDepth--; continue; }
        newline(baseIndent + 1);
        put(tok, text);
        continue;
      }
      if (JOIN_KW.has(upper)) {
        const prevWasJoin = prev && prev.t === 'kw' && JOIN_KW.has(prev.v.toUpperCase());
        if (!prevWasJoin) newline(baseIndent);
        put(tok, text);
        currentClause = 'JOIN';
        continue;
      }
      if (CLAUSE_KW.has(upper)) {
        newline(baseIndent);
        put(tok, text);
        currentClause = upper;
        continue;
      }
      put(tok, text);
      continue;
    }

    if (tok.t === 'punct') {
      const v = tok.v;
      if (v === ',') {
        put(tok, text); // attaches to the previous token (no leading space)
        if (inlineParenDepth === 0 && currentClause !== 'LIMIT') newline(baseIndent + 1);
        continue;
      }
      if (v === '(') {
        const nextTok = toks[idx + 1];
        const nextUpper = nextTok && nextTok.t === 'kw' ? nextTok.v.toUpperCase() : null;
        const breakOpen = nextUpper === 'SELECT' || nextUpper === 'WITH'; // subquery / CTE
        // A '(' after a table name (INSERT INTO t (…), CREATE TABLE t (…),
        // UPDATE t (…)) keeps a space; a '(' after a function name sticks.
        const tableParen = prev && prev.t === 'ident' && prev2 && prev2.t === 'kw' &&
          ['INTO', 'TABLE', 'UPDATE'].includes(prev2.v.toUpperCase());
        put(tok, text, tableParen ? true : undefined);
        if (breakOpen) {
          frames.push({ indent: baseIndent, broken: true });
          baseIndent += 1;
          newline(baseIndent);
        } else {
          frames.push({ indent: baseIndent, broken: false });
          inlineParenDepth++;
        }
        continue;
      }
      if (v === ')') {
        const fr = frames.pop();
        if (fr && fr.broken) { baseIndent = fr.indent; newline(baseIndent); put(tok, text); }
        else { if (inlineParenDepth > 0) inlineParenDepth--; put(tok, text); }
        continue;
      }
      if (v === '-' || v === '+') {
        const unary = !isValueToken(prev);
        put(tok, text);
        if (unary) attachNext = true; // glue the sign to its operand: -1, not - 1
        continue;
      }
      put(tok, text);
      continue;
    }

    // ident / number / string / dquote / param / var / blockComment
    put(tok, text);
  }

  lines.push(line.replace(/[ \t]+$/, ''));
  return lines.join('\n').replace(/^\n+/, '').replace(/\s+$/, '');
}

// Flat printer (no clause reflow): tokens joined with the same spacing rules as
// the full printer (no space before , ; ) . — function '(' sticks — unary +/-
// glue to their operand). Two modes via preserveLines:
//   • true  → MINIMAL: replay the writer's existing line breaks + indentation,
//     never adding or removing a newline (auto-format-on-';' uses this so it
//     doesn't fight your layout).
//   • false → ONE-LINE: emit NO newlines, collapsing the statement onto one line.
//     The caller (formatOneLine) refuses statements carrying a line comment, so a
//     `-- x` can never swallow the rest of the line; block comments (`/* x */`)
//     survive verbatim inline.
// Either way only whitespace changes, so the signature invariant in
// formatStatement holds.
function printFlat(toks, opts, preserveLines) {
  const out = [];
  let line = '';
  let prev = null;
  let attachNext = false; // unary sign: suppress the space before the next token
  let started = false;
  const flush = () => out.push(line.replace(/[ \t]+$/, ''));
  for (const tok of toks) {
    const text = render(tok, opts);
    if (preserveLines && started && tok.nl > 0) {
      flush();
      for (let k = 1; k < tok.nl; k++) out.push(''); // preserve blank lines
      line = (tok.ind || '') + text;
    } else if (!started) {
      line = text;
    } else {
      const sp = !attachNext && spaceBetween(prev, tok);
      line += (sp ? ' ' : '') + text;
    }
    attachNext = tok.t === 'punct' && (tok.v === '-' || tok.v === '+') && !isValueToken(prev);
    prev = tok;
    started = true;
  }
  flush();
  return out.join('\n').replace(/\s+$/, '');
}
const printMinimal = (toks, opts) => printFlat(toks, opts, true);
const printOneLine = (toks, opts) => printFlat(toks, opts, false);

// True if the SQL contains a `--` / `#` line comment (which cannot survive being
// collapsed onto one line — it would comment out everything after it).
function hasLineComment(sql) {
  return tokenize(sql).some((t) => t.t === 'lineComment');
}

// ── Procedural re-indenter (BEGIN/IF/CASE/WHILE/LOOP/REPEAT) ───────────────────
// Tier-1 "indent routine body": preserve the writer's existing line breaks and
// only recompute each line's LEADING indentation from a block-depth stack, then
// render each line with the shared one-line spacing rules. Because it touches
// only whitespace + keyword case, the signature invariant in formatStatement
// still guards it (a mis-parsed exotic construct reverts to the original).

// One indentation level per open block frame. Branch-bearing frames (if/case)
// and the repeat loop contribute their body level only while a branch is open,
// so ELSE/WHEN/UNTIL lines dedent to the opener.
function frameLevel(f) {
  switch (f.type) {
    case 'begin': case 'while': case 'loop': return 1;
    case 'repeat': return f.open ? 1 : 0;       // body open until UNTIL
    case 'if':     return f.open ? 1 : 0;       // body open after THEN/ELSE
    case 'case':   return 1 + (f.open ? 1 : 0); // WHEN level + THEN/ELSE body
    default:       return 0;
  }
}
function depthOf(stack) { let d = 0; for (const f of stack) d += frameLevel(f); return d; }

// The block keywords on one line, in order, with `END X` merged into a single
// END closer (so the trailing IF/LOOP/… isn't mistaken for a new opener).
function blockSeq(lineToks) {
  const kws = [];
  for (const t of lineToks) if (t.t === 'kw') kws.push(t.v.toUpperCase());
  const BLOCK = new Set(['BEGIN','IF','CASE','WHILE','LOOP','REPEAT','THEN','DO','ELSE','ELSEIF','ELSIF','WHEN','UNTIL','END']);
  const ENDWORD = new Set(['IF','WHILE','LOOP','CASE','REPEAT']);
  const seq = [];
  for (let a = 0; a < kws.length; a++) {
    const u = kws[a];
    if (u === 'END') { if (ENDWORD.has(kws[a + 1])) a++; seq.push('END'); continue; }
    if (BLOCK.has(u)) seq.push(u);
  }
  return seq;
}

// Continuation/closer keywords that, when they LEAD a line, align with the block
// opener (dedent) rather than the body.
const PROC_CONT = new Set(['END', 'ELSE', 'ELSEIF', 'ELSIF', 'WHEN', 'UNTIL']);

// Walk one line's block keywords, mutating the stack, and return the indent level
// the line should print at. Openers push (affecting LATER lines); a leading
// continuation/closer adjusts the stack first so the keyword aligns with its
// opener.
function layoutLine(lineToks, stack) {
  const seq = blockSeq(lineToks);
  const top = () => stack[stack.length - 1];
  let lineIndent = null;
  for (let p = 0; p < seq.length; p++) {
    const s = seq[p];
    if (p === 0 && PROC_CONT.has(s)) {
      if (s === 'END') { if (stack.length) stack.pop(); }
      else if (s === 'UNTIL') { const f = top(); if (f && f.type === 'repeat') f.open = false; }
      else { const f = top(); if (f && (f.type === 'if' || f.type === 'case')) f.open = false; }
      lineIndent = depthOf(stack);
      if (s === 'ELSE') { const f = top(); if (f && (f.type === 'if' || f.type === 'case')) f.open = true; }
      continue; // ELSEIF/WHEN reopen at their THEN (handled below); END/UNTIL: nothing more
    }
    if (p === 0) lineIndent = depthOf(stack);
    switch (s) {
      case 'BEGIN':  stack.push({ type: 'begin' }); break;
      case 'IF':     stack.push({ type: 'if', open: false }); break;
      case 'CASE':   stack.push({ type: 'case', open: false }); break;
      case 'WHILE':  stack.push({ type: 'while' }); break;
      case 'LOOP':   stack.push({ type: 'loop' }); break;
      case 'REPEAT': stack.push({ type: 'repeat', open: true }); break;
      case 'THEN': case 'DO': case 'ELSE': { const f = top(); if (f && (f.type === 'if' || f.type === 'case')) f.open = true; break; }
      case 'ELSEIF': case 'ELSIF': { const f = top(); if (f && f.type === 'if') f.open = false; break; } // THEN reopens
      case 'WHEN':   { const f = top(); if (f && f.type === 'case') f.open = false; break; }            // THEN reopens
      case 'END':    if (stack.length) stack.pop(); break;
      case 'UNTIL':  { const f = top(); if (f && f.type === 'repeat') f.open = false; break; }
    }
  }
  if (lineIndent === null) lineIndent = depthOf(stack);
  return Math.max(0, lineIndent);
}

// Re-indent a (procedural) statement: keep its line breaks, recompute leading
// indentation from block nesting, render each line with the one-line spacing
// rules. Strings/comments stay verbatim (their internal newlines live inside the
// token value, so they're never touched).
function printProcedural(toks, opts) {
  const IND = opts.indent || '  ';
  // Group tokens into physical source lines; `blanks` = blank lines before each.
  const lines = [];
  let cur = [], curBlanks = 0;
  for (let k = 0; k < toks.length; k++) {
    const t = toks[k];
    if (k > 0 && t.nl > 0) { lines.push({ toks: cur, blanks: curBlanks }); cur = []; curBlanks = t.nl - 1; }
    cur.push(t);
  }
  if (cur.length) lines.push({ toks: cur, blanks: curBlanks });

  const stack = [];
  const out = [];
  for (const ln of lines) {
    for (let b = 0; b < ln.blanks; b++) out.push('');
    const indent = layoutLine(ln.toks, stack);
    const body = printFlat(ln.toks, opts, false); // one-line render of this line
    out.push(body ? IND.repeat(indent) + body : '');
  }
  return out.join('\n').replace(/\s+$/, '');
}

// ── Statement / script segmentation ──────────────────────────────────────────
// Splits the source at top-level statement terminators, quote/comment/backtick
// aware, honouring DELIMITER directives. Statements parsed under a non-';'
// delimiter are flagged opaque (they're routine bodies). The raw bytes of each
// statement are kept (we only reflow the ones we choose to format).
function segment(sql) {
  const items = [];
  let i = 0, start = 0;
  let delim = ';';
  const n = sql.length;
  let inS = false, inD = false, inB = false, inLine = false, inBlock = false;

  const pushStmt = (end, hadTerm) => {
    const body = sql.slice(start, end);
    if (body.trim() !== '') items.push({ type: 'stmt', body, start, terminator: delim, hadTerminator: hadTerm, opaque: delim !== ';' });
  };

  while (i < n) {
    const c = sql[i], nx = sql[i + 1];
    if (inLine)  { if (c === '\n') inLine = false; i++; continue; }
    if (inBlock) { if (c === '*' && nx === '/') { inBlock = false; i += 2; continue; } i++; continue; }
    if (inS) { if (c === '\\' && nx) { i += 2; continue; } if (c === "'") inS = false; i++; continue; }
    if (inD) { if (c === '\\' && nx) { i += 2; continue; } if (c === '"') inD = false; i++; continue; }
    if (inB) { if (c === '`') inB = false; i++; continue; }

    // DELIMITER directive — only at a line start with no pending statement.
    if ((c === 'd' || c === 'D') && (i === 0 || sql[i - 1] === '\n') && sql.slice(start, i).trim() === '') {
      const m = /^delimiter[ \t]+(\S+)[ \t]*\r?(?:\n|$)/i.exec(sql.slice(i));
      if (m) {
        const lineEnd = sql.indexOf('\n', i);
        items.push({ type: 'delimiter', text: sql.slice(i, lineEnd === -1 ? n : lineEnd).replace(/\s+$/, '') });
        delim = m[1];
        i = lineEnd === -1 ? n : lineEnd + 1;
        start = i;
        continue;
      }
    }

    if (c === '-' && nx === '-') { inLine = true; i += 2; continue; }
    if (c === '#') { inLine = true; i++; continue; }
    if (c === '/' && nx === '*') { inBlock = true; i += 2; continue; }
    if (c === "'") { inS = true; i++; continue; }
    if (c === '"') { inD = true; i++; continue; }
    if (c === '`') { inB = true; i++; continue; }

    if (sql.startsWith(delim, i)) { pushStmt(i, true); i += delim.length; start = i; continue; }
    i++;
  }
  if (start < n && sql.slice(start).trim() !== '') {
    items.push({ type: 'stmt', body: sql.slice(start), start, terminator: '', hadTerminator: false, opaque: delim !== ';' });
  }
  return items;
}

// Strip leading whitespace + comments so the first real keyword can be tested.
function stripLeading(s) {
  for (;;) {
    const t = s.replace(/^\s+/, '');
    if (t.startsWith('--') || t.startsWith('#')) { const nl = t.indexOf('\n'); s = nl === -1 ? '' : t.slice(nl + 1); continue; }
    if (t.startsWith('/*')) { const e = t.indexOf('*/'); s = e === -1 ? '' : t.slice(e + 2); continue; }
    return t;
  }
}

// A routine/DDL whose body must not be reflowed (procedural BEGIN…END etc.).
export function isOpaqueStatement(body) {
  const head = stripLeading(body).slice(0, 120).toUpperCase();
  return /^(CREATE|ALTER)\b/.test(head) && /\b(PROCEDURE|FUNCTION|TRIGGER|EVENT)\b/.test(head);
}

// Format ONE statement body (no terminator). Returns the original unchanged if
// the safety invariant trips.
export function formatStatement(body, opts = {}) {
  const o = normOpts(opts);
  const trimmed = body.trim();
  if (trimmed === '') return '';
  const toks = tokenize(trimmed);
  if (toks.length === 0) return trimmed;
  const printed = o.mode === 'oneline' ? printOneLine(toks, o)
    : o.mode === 'indent' ? printProcedural(toks, o)
    : (o.lineBreaks ? printTokens : printMinimal)(toks, o);
  if (signature(trimmed) !== signature(printed)) return trimmed; // safety: never corrupt
  return printed;
}

// mode: 'case' → keyword-case only (no whitespace changes). Otherwise lineBreaks
// true (default) → full multi-line reflow; false → minimal (case + spacing,
// existing line breaks preserved).
function normOpts(opts) {
  return {
    keywordCase: opts.keywordCase || 'upper',
    indent: opts.indent || '  ',
    lineBreaks: opts.lineBreaks !== false,
    mode: opts.mode || '',
  };
}

// Keyword-case ONLY: re-case keyword tokens in place and leave every other byte
// exactly as typed — whitespace, identifiers, strings, comments, even routine
// bodies. The smallest possible "format" (a minimal diff). Works on the whole
// document in one pass (no statement segmentation): re-casing a keyword is safe
// even inside a DELIMITER/BEGIN…END block. The signature invariant still guards.
function recaseDocument(sql, opts) {
  const toks = tokenize(sql);
  let out = '', pos = 0;
  for (const t of toks) {
    out += sql.slice(pos, t.start);                                  // gap before token, verbatim
    out += t.t === 'kw' ? render(t, opts) : sql.slice(t.start, t.end); // token: cased if keyword, else verbatim
    pos = t.end;
  }
  out += sql.slice(pos);                                             // trailing bytes
  return signature(sql) === signature(out) ? out : sql;             // safety: never corrupt
}

// ── Schema-aware column re-casing ─────────────────────────────────────────────
// A case-only normaliser run BEFORE formatSql, so the formatter's own signature
// guard (which forbids identifier changes) still holds on the already-recased
// text. Only COLUMN references the caller recognises (passed as a lowercased name
// set) are touched — table names are intentionally left alone (case-sensitive on
// some servers). Rule: an underscore name → all-lowercase; a single word →
// TitleCase. Skipped: backtick-quoted names, a qualifier before '.' (table /
// alias), and function calls (ident immediately before '(').
export function recaseColumnName(name) {
  if (name.includes('_')) return name.toLowerCase();
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
}

// caseMap (optional) is the user's GLOBAL rename dictionary as lowercased(name) →
// canonical spelling (case-only). When a column is in the dictionary its canonical
// spelling wins; otherwise we fall back to the schema-driven heuristic for columns
// the columnSet knows. A dictionary value that isn't a pure case variant is
// ignored (defensive — the server only stores case-only entries).
export function recaseColumns(sql, columnSet, caseMap) {
  const hasSet = columnSet && columnSet.size > 0;
  const hasMap = caseMap && caseMap.size > 0;
  if (!sql || (!hasSet && !hasMap)) return sql;
  const toks = tokenize(sql);
  let out = '', pos = 0;
  for (let idx = 0; idx < toks.length; idx++) {
    const t = toks[idx];
    out += sql.slice(pos, t.start);
    let v = sql.slice(t.start, t.end);
    const next = toks[idx + 1];
    const isQualifier = next && next.t === 'punct' && next.v === '.'; // a.col → 'a' is table/alias
    const isFuncCall  = next && next.t === 'punct' && next.v === '('; // f(…) → 'f' is a function
    if (t.t === 'ident' && v.charCodeAt(0) !== 96 /* ` */ && !isQualifier && !isFuncCall) {
      const lower = v.toLowerCase();
      const mapped = hasMap ? caseMap.get(lower) : undefined;
      if (mapped && mapped.toLowerCase() === lower) v = mapped;          // dictionary canonical (case-only)
      else if (hasSet && columnSet.has(lower)) v = recaseColumnName(v);  // schema heuristic fallback
    }
    out += v;
    pos = t.end;
  }
  out += sql.slice(pos);
  // Safety: we only ever flip letter case, so case-folding must be identical to
  // the input — otherwise something drifted and we return the original untouched.
  return out.toLowerCase() === sql.toLowerCase() ? out : sql;
}

// Join segmented {kind:'stmt'|'delim', text} pieces: a blank line between two
// adjacent statements, a single newline around DELIMITER directives.
function joinPieces(pieces) {
  let out = '';
  for (let k = 0; k < pieces.length; k++) {
    if (k === 0) { out = pieces[k].text; continue; }
    const sep = pieces[k].kind === 'stmt' && pieces[k - 1].kind === 'stmt' ? '\n\n' : '\n';
    out += sep + pieces[k].text;
  }
  return out;
}

// ── Public entry point ────────────────────────────────────────────────────────
// formatSql(sql, { keywordCase: 'upper'|'lower'|'preserve', indent: '  ' })
// Multi-statement aware: formats each ';'-terminated statement, leaves DELIMITER
// blocks and routine bodies opaque, and preserves whether a trailing terminator
// was present.
export function formatSql(sql, opts = {}) {
  const o = normOpts(opts);
  if (!sql || !sql.trim()) return sql;
  if (o.mode === 'case') return recaseDocument(sql, o); // keyword-case only, no reflow
  const items = segment(sql);
  const pieces = [];
  for (const it of items) {
    if (it.type === 'delimiter') { pieces.push({ kind: 'delim', text: it.text }); continue; }
    const opaque = it.opaque || isOpaqueStatement(it.body);
    const bodyOut = opaque ? it.body.trim() : formatStatement(it.body, o);
    pieces.push({ kind: 'stmt', text: bodyOut + (it.hadTerminator ? it.terminator : '') });
  }
  return joinPieces(pieces);
}

// formatOneLine(sql) — collapse each ';'-separated statement onto a SINGLE line
// (uppercased keywords + single-space spacing, no newlines). Returns
// { text, skipped } where `skipped` counts statements left untouched because they
// can't safely be single-lined: a routine/DELIMITER body (opaque) or a statement
// carrying a `--`/`#` line comment. Each collapsed statement is still guarded by
// the token-signature invariant (via formatStatement), so it can never corrupt
// SQL. Statements are rejoined one-per-line; a trailing terminator is preserved.
export function formatOneLine(sql, opts = {}) {
  const o = normOpts({ ...opts, mode: 'oneline' });
  if (!sql || !sql.trim()) return { text: sql, skipped: 0 };
  const items = segment(sql);
  const pieces = [];
  let skipped = 0;
  for (const it of items) {
    if (it.type === 'delimiter') { pieces.push(it.text); continue; }
    const body = it.body.trim();
    const term = it.hadTerminator ? it.terminator : '';
    const opaque = it.opaque || isOpaqueStatement(body);
    if (opaque || hasLineComment(body)) { pieces.push(body + term); skipped++; continue; }
    pieces.push(formatStatement(body, o) + term);
  }
  return { text: pieces.join('\n'), skipped };
}

// A `DELIMITER x` directive anywhere in the script (line-leading).
const DELIM_DIRECTIVE = /(^|\n)\s*DELIMITER\s+\S/i;

// formatRoutineIndent(sql) — Tier-1 "indent routine body": re-indent the
// procedural body (BEGIN/IF/CASE/WHILE/LOOP/REPEAT) of each stored
// routine/trigger/event, preserving its line breaks. Returns { text, routines }
// where `routines` counts how many routine bodies were re-indented (0 ⇒ the
// caller can tell the user there was nothing to indent). Non-routine statements
// are left exactly as-is. Each body is signature-guarded via formatStatement.
export function formatRoutineIndent(sql, opts = {}) {
  const o = normOpts({ ...opts, mode: 'indent' });
  if (!sql || !sql.trim()) return { text: sql, routines: 0 };

  // Without a DELIMITER directive a routine body CAN'T be safely ';'-segmented —
  // its BEGIN…END is full of ';'. So find where the routine starts
  // (CREATE/ALTER … PROCEDURE/FUNCTION/TRIGGER/EVENT) and treat everything from
  // there to the end of the buffer as ONE opaque body. Anything before it — the
  // ScriptEditor's standard `DROP PROCEDURE IF EXISTS …;` prefix — is a leading
  // statement, left exactly as-is. (Previously this fell through to segment(),
  // which split the routine at its internal ';' → blank lines + lost nesting.)
  if (!DELIM_DIRECTIVE.test(sql)) {
    const items = segment(sql);
    const idx = items.findIndex((it) => it.type === 'stmt' && (it.opaque || isOpaqueStatement(it.body)));
    if (idx < 0) return { text: sql, routines: 0 }; // nothing routine-shaped to indent
    const routineStart = items[idx].start;
    const lead = sql.slice(0, routineStart).replace(/\s+$/, '');
    const routine = formatStatement(sql.slice(routineStart).trim(), o);
    return { text: lead ? lead + '\n' + routine : routine, routines: 1 };
  }

  // DELIMITER present: segment honours it, so routine bodies (delim ≠ ';') stay
  // whole. Indent each routine; leave normal statements untouched.
  const items = segment(sql);
  const pieces = [];
  let routines = 0;
  for (const it of items) {
    if (it.type === 'delimiter') { pieces.push({ kind: 'delim', text: it.text }); continue; }
    const body = it.body.trim();
    const term = it.hadTerminator ? it.terminator : '';
    const isRoutine = it.opaque || isOpaqueStatement(body);
    if (isRoutine) { routines++; pieces.push({ kind: 'stmt', text: formatStatement(body, o) + term }); }
    else pieces.push({ kind: 'stmt', text: body + term }); // leave normal statements untouched
  }
  return { text: joinPieces(pieces), routines };
}
